import React, { useMemo } from 'react';

import { Checkbox, DatePicker, Radio, Select, Tooltip } from 'antd';
import Notice from 'components/common/Notice/Notice';
import PlanActualSegmented from 'components/common/PlanActualSegmented';
import { COPY_TOOL_REPLACE_PARTIAL_TOOLTIP } from 'constants/messageConstants';
import {
  COPY_TOOL_COPY_MODE_ADD_AS_NEW_HINT,
  COPY_TOOL_COPY_MODE_ADD_AS_NEW_LABEL,
  COPY_TOOL_COPY_MODE_ADD_TO_HINT,
  COPY_TOOL_COPY_MODE_ADD_TO_LABEL,
  COPY_TOOL_COPY_MODE_LABEL,
  COPY_TOOL_COPY_MODE_REPLACE_HINT,
  COPY_TOOL_COPY_MODE_REPLACE_LABEL,
  COPY_TOOL_DATA_LABEL,
  COPY_TOOL_DATE_OPTION_LABELS,
  COPY_TOOL_DATES_LABEL,
  COPY_TOOL_KEEP_AREA_HINT,
  COPY_TOOL_KEEP_AREA_LABEL,
  COPY_TOOL_OPTIONS_LABEL,
  COPY_TOOL_YEAR_ARIA_LABEL,
  COPY_TOOL_YEAR_LABEL,
} from 'constants/uiConstants';
import dayjs, { Dayjs } from 'dayjs';
import {
  CopyDateOption,
  CopyMode,
  ICopyOptions,
} from 'interfaces/ICopyPaddockData';
import { InputTaskCategory } from 'interfaces/IInputTask';

const COPY_MODE_HINTS: Record<CopyMode, (mode: string) => string> = {
  [CopyMode.Replace]: COPY_TOOL_COPY_MODE_REPLACE_HINT,
  [CopyMode.AddTo]: COPY_TOOL_COPY_MODE_ADD_TO_HINT,
  [CopyMode.AddAsNew]: () => COPY_TOOL_COPY_MODE_ADD_AS_NEW_HINT,
};

const DATE_OPTIONS = [
  CopyDateOption.Shift,
  CopyDateOption.Keep,
  CopyDateOption.Remove,
].map((option) => ({
  value: option,
  label: COPY_TOOL_DATE_OPTION_LABELS[option],
}));

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-xs font-semibold text-gray-dark">{children}</span>
);

interface CopySideControlsProps {
  /** Source or Destination; the legend and the year field's name. */
  side: string;
  year: number;
  onYearChange: (year: number) => void;
  mode: InputTaskCategory;
  onModeChange: (mode: InputTaskCategory) => void;
}

/** One side's year filter and Plan/Actual choice, above the panels. */
export const CopySideControls: React.FC<CopySideControlsProps> = ({
  side,
  year,
  onYearChange,
  mode,
  onModeChange,
}) => {
  // Stable identity, or rc-picker resets its open panel on every render (OP-2285).
  const value = useMemo(() => dayjs().year(year).startOf('year'), [year]);

  return (
    <fieldset className="flex flex-col gap-1">
      <legend className="mb-1 text-sm font-semibold text-charcoal">
        {side}
      </legend>
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex flex-col gap-1">
          <FieldLabel>{COPY_TOOL_YEAR_LABEL}</FieldLabel>
          <DatePicker
            picker="year"
            aria-label={COPY_TOOL_YEAR_ARIA_LABEL(side)}
            value={value}
            onChange={(next: Dayjs | null) => next && onYearChange(next.year())}
            allowClear={false}
            inputReadOnly
            size="small"
            className="w-20"
          />
        </label>
        <div className="flex flex-col gap-1">
          <FieldLabel>{COPY_TOOL_DATA_LABEL}</FieldLabel>
          <PlanActualSegmented value={mode} onChange={onModeChange} />
        </div>
      </div>
    </fieldset>
  );
};

interface CopyModeControlProps {
  value: CopyMode;
  onChange: (mode: CopyMode) => void;
  /** Destination's Plan or Actual label; Replace and Add to only touch that data. */
  destinationModeLabel: string;
  /** True for a partial copy; choosing Replace ticks everything again. */
  isPartialCopy: boolean;
  /** Shown under the hint, e.g. why the mode changed by itself. */
  note?: string;
}

/** Replace / Add to / Add as new, with what the chosen mode does. */
export const CopyModeControl: React.FC<CopyModeControlProps> = ({
  value,
  onChange,
  destinationModeLabel,
  isPartialCopy,
  note,
}) => {
  const options = useMemo(
    () => [
      {
        value: CopyMode.Replace,
        label: (
          <Tooltip
            title={isPartialCopy ? COPY_TOOL_REPLACE_PARTIAL_TOOLTIP : null}
          >
            <span>{COPY_TOOL_COPY_MODE_REPLACE_LABEL}</span>
          </Tooltip>
        ),
      },
      { label: COPY_TOOL_COPY_MODE_ADD_TO_LABEL, value: CopyMode.AddTo },
      { label: COPY_TOOL_COPY_MODE_ADD_AS_NEW_LABEL, value: CopyMode.AddAsNew },
    ],
    [isPartialCopy]
  );

  return (
    <fieldset className="flex min-w-0 flex-[2] flex-col gap-1 rounded-md border border-gray-medium p-2">
      <legend className="mb-1 px-2 text-sm font-semibold text-charcoal">
        {COPY_TOOL_COPY_MODE_LABEL}
      </legend>
      <Radio.Group
        value={value}
        onChange={(event) => onChange(event.target.value as CopyMode)}
        options={options}
        className="flex flex-col gap-1"
      />
      <span className="text-xs text-gray-muted">
        {COPY_MODE_HINTS[value](destinationModeLabel)}
      </span>
      {note && (
        <Notice role="note" className="mt-1 w-fit">
          {note}
        </Notice>
      )}
    </fieldset>
  );
};

interface CopyOptionsControlProps {
  value: ICopyOptions;
  onChange: (options: ICopyOptions) => void;
}

/** Keep area and how dates are copied, applied to every destination. */
export const CopyOptionsControl: React.FC<CopyOptionsControlProps> = ({
  value,
  onChange,
}) => (
  <fieldset className="flex min-w-0 flex-1 flex-col gap-1 rounded-md border border-gray-medium p-2">
    <legend className="mb-1 px-2 text-sm font-semibold text-charcoal">
      {COPY_TOOL_OPTIONS_LABEL}
    </legend>
    <div className="flex flex-col items-start gap-4">
      <label className="flex w-full flex-1 flex-col gap-1">
        <FieldLabel>{COPY_TOOL_DATES_LABEL}</FieldLabel>
        <Select
          size="small"
          value={value.dateOption}
          options={DATE_OPTIONS}
          onChange={(dateOption: CopyDateOption) =>
            onChange({ ...value, dateOption })
          }
        />
      </label>
      <Checkbox
        checked={value.keepArea}
        onChange={(event) =>
          onChange({ ...value, keepArea: event.target.checked })
        }
      >
        {COPY_TOOL_KEEP_AREA_LABEL}
      </Checkbox>
    </div>
    <span className="text-xs text-gray-muted">{COPY_TOOL_KEEP_AREA_HINT}</span>
  </fieldset>
);
